import { Box, HStack, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { SiSublimetext } from 'react-icons/si'
import { getDateTimeReadFormat, getDateTimeToSubmit } from 'shared/utils/dateFormatting'
import {
  Button,
  InputDate,
  Select,
  Snippets,
  SnippetsHeading,
  SnippetsItem,
  SnippetsList,
} from 'uikit'
import { HeadingItem, ItemIcon, ItemLink, ItemText } from 'uikit/Snippets'
import { toaster } from 'uikit/Toaster'
import {
  Container,
  FilterFooter,
  FilterStack,
  FilterStackGroup,
  SnippetsContainer,
  SnippetsContainerMain,
  SnippetsContainerRightAside,
} from 'uikit/layout'
import { ActionButton } from './elements/ActionButton'
import { useFetchReferralTransfer } from './services/useFetchReferralTransfer'
import { useTransferProcess } from './services/useTransferProcess'
import { useTransferRefund } from './services/useTransferRefund'

const initFilter = {
  transferStatus: '',
  createdStart: '',
  createdEnd: '',
  processedStart: '',
  processedEnd: '',
}

export const Transfer = () => {
  const { mutate: getReferralTransfer, data, isPending: isLoading } = useFetchReferralTransfer()
  const { mutate: setProcess } = useTransferProcess()
  const { mutate: setRefund } = useTransferRefund()

  const [filter, setFilter] = useState(initFilter)

  const updateList = () => {
    const dataPagination = {
      page: '0',
      size: '10000',
    }

    getReferralTransfer({
      ...dataPagination,
      ...(filter.transferStatus !== '' && { transferStatus: filter.transferStatus }),
      ...(filter.createdStart !== '' && {
        createdStart: getDateTimeToSubmit(filter.createdStart),
      }),
      ...(filter.createdEnd !== '' && { createdEnd: getDateTimeToSubmit(filter.createdEnd) }),
      ...(filter.processedStart !== '' && {
        processedStart: getDateTimeToSubmit(filter.processedStart),
      }),
      ...(filter.processedEnd !== '' && {
        processedEnd: getDateTimeToSubmit(filter.processedEnd),
      }),
    })
  }

  const handleFilter = (value, field) => {
    setFilter((e) => ({
      ...e,
      [field]: value,
    }))
  }

  useEffect(() => {
    updateList()
  }, [filter])

  const handleProcessClick = (id) => {
    setProcess(id, {
      onSuccess: () => {
        toaster.create({
          description: 'Успешно зачислено на счет',
          type: 'success',
        })
        updateList()
      },
    })
  }

  const handleRefundClick = (id) => {
    setRefund(id, {
      onSuccess: () => {
        toaster.create({
          description: 'Успешно отказано',
          type: 'success',
        })
        updateList()
      },
    })
  }

  const handleResetFilter = () => {
    setFilter({ ...initFilter })
  }

  return (
    <Container>
      <SnippetsContainer indentTop={120}>
        <SnippetsContainerMain>
          <Snippets
            columns="42px 80px minmax(180px, 1fr) 180px 180px 200px 200px 200px 230px"
            itemWidth="1600px"
          >
            <SnippetsHeading>
              <HeadingItem />
              <HeadingItem>N</HeadingItem>
              <HeadingItem>Пользователь</HeadingItem>
              <HeadingItem>Бонусы</HeadingItem>
              <HeadingItem>Сумма, ₽</HeadingItem>
              <HeadingItem>Дата операции</HeadingItem>
              <HeadingItem>Дата запроса</HeadingItem>
              <HeadingItem>Статус</HeadingItem>
            </SnippetsHeading>
            <SnippetsList
              activePage={0}
              totalPages={0}
              data={data}
              keyField="id"
              isLoading={isLoading}
            >
              {(item) => {
                const status = {
                  '-1': 'Отклонен',
                  0: 'Не проведен',
                  1: 'Проведен',
                }

                return (
                  <SnippetsItem to={`/user/phone=${item?.customerId}`}>
                    <ItemIcon IconComponent={SiSublimetext} />
                    <ItemLink>{item?.rowId}</ItemLink>
                    <ItemLink>{item?.customerId}</ItemLink>
                    <ItemText>{`${item?.amount}`}</ItemText>
                    <ItemText>{`${item?.amountRub}`}</ItemText>
                    <ItemText>{getDateTimeReadFormat(item?.processed)}</ItemText>
                    <ItemText>{getDateTimeReadFormat(item?.created)}</ItemText>
                    <ItemText>{status[String(item?.transferStatus)]}</ItemText>
                    <HStack gap="10px">
                      <ActionButton
                        colorPalette="green"
                        disabled={item?.transferStatus === -1 || item?.transferStatus === 1}
                        onClick={() => handleProcessClick(item?.id)}
                      >
                        Зачислить
                      </ActionButton>
                      <ActionButton
                        disabled={item?.transferStatus === -1 || item?.transferStatus === 1}
                        onClick={() => handleRefundClick(item?.id)}
                      >
                        Отклонить
                      </ActionButton>
                    </HStack>
                  </SnippetsItem>
                )
              }}
            </SnippetsList>
          </Snippets>
        </SnippetsContainerMain>
        <SnippetsContainerRightAside>
          <FilterStack>
            <Select
              name="transferStatus"
              label="Статус"
              isSearchable={false}
              placeholder="Выберите"
              value={filter.transferStatus}
              options={[
                {
                  value: '',
                  label: 'Все',
                },
                {
                  value: '0',
                  label: 'Не проведен',
                },
                {
                  value: '1',
                  label: 'Проведен',
                },
                {
                  value: '-1',
                  label: 'Отклонен',
                },
              ]}
              onChange={(e) => handleFilter(e.value, 'transferStatus')}
            />
            <FilterStackGroup>
              <InputDate
                value={filter.createdStart}
                dayPickerProps={{
                  showCurrentDate: false,
                }}
                name="createdStartFilter"
                placement="bottom"
                onChange={(e) => handleFilter(e, 'createdStart')}
                label="Дата запроса"
                placeholder="C"
              />
              <InputDate
                value={filter.createdEnd}
                dayPickerProps={{
                  showCurrentDate: false,
                }}
                name="createdEndFilter"
                placement="bottom"
                onChange={(e) => handleFilter(e, 'createdEnd')}
                placeholder="По"
              />
            </FilterStackGroup>
            <FilterStackGroup>
              <InputDate
                value={filter.processedStart}
                dayPickerProps={{
                  showCurrentDate: false,
                }}
                name="processedStartFilter"
                placement="bottom"
                onChange={(e) => handleFilter(e, 'processedStart')}
                label="Дата операции"
                placeholder="C"
              />
              <InputDate
                value={filter.processedEnd}
                dayPickerProps={{
                  showCurrentDate: false,
                }}
                name="processedEndFilter"
                placement="bottom"
                onChange={(e) => handleFilter(e, 'processedEnd')}
                placeholder="По"
              />
            </FilterStackGroup>
          </FilterStack>
          <FilterFooter pb="10px">
            <Stack gap={2} width="100%">
              <Box>
                <Button size="sm" width="100%" onClick={handleResetFilter}>
                  Очистить фильтр
                </Button>
                <Text
                  fontSize="0.8rem"
                  color="gray.300"
                  mt={2}
                  textAlign="center"
                >{`Найдено: ${data?.length}`}</Text>
              </Box>
            </Stack>
          </FilterFooter>
        </SnippetsContainerRightAside>
      </SnippetsContainer>
    </Container>
  )
}
